import { useMemo, useState, type ReactNode } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react/dist/csr/CaretDown';
import { CaretUpIcon } from '@phosphor-icons/react/dist/csr/CaretUp';
import { useTranslation } from 'react-i18next';

import { AuditEntryDetail } from './AuditEntryDetail';
import { hasDiff } from './auditDiff';
import { DataTable, type Column, type ListStatus, type Row } from '../../design-system/composites/DataTable/DataTable';
import type { NoResultsCopy } from '../../design-system/composites/DataTable/states/NoResultsState';
import { Button } from '../../design-system/primitives/Button/Button';
import type { AuditEntry } from '../../lib/api/client';
import { formatRelative, formatTimestamp } from '../../lib/format/dateTime';
import styles from './AuditEntryTable.module.css';

/**
 * The rows behind `GET /audit-log` and `GET /profile/activity`, rendered — and
 * NOTHING else. No fetch, no filters, no cursor: `AuditLogScreen` (task 7) and
 * a later profile activity screen (task 8) both own those, and both hand this
 * component the one page of `AuditEntry[]` they already have. Task 8's own
 * brief is explicit that this component is reused UNCHANGED — a column it
 * needs hidden is `hideActorColumn`, a prop, not a fork of this file.
 *
 * ## Grouping is the whole reason this file exists (decision V-8)
 *
 * M-02a's observer means one administrator action commonly writes TWO rows to
 * `audit_log`: a semantic one (`user.deactivated`) and the observer's own
 * field-level diff (`user.updated`), both carrying the same `request_id` —
 * the one column that ties a single action back together. Rendering them as
 * two table rows misrepresents what happened: an administrator reading the
 * trail sees two events where there was one click. `groupByRequestId` below
 * folds every row that shares a `request_id` into one table row; a row whose
 * `request_id` is `null` (nothing else could share it) is its own group of
 * one, keyed on its own `uuid` so it never collides with a real request id.
 *
 * Order is preserved from the API response — the FIRST entry seen for a
 * group is that group's primary (its timestamp, actor and entity populate
 * the row), and the Action column lists every action in the group in the
 * order they arrived, not sorted or deduplicated. The API returns rows
 * newest-first per its own index, and within one request the semantic write
 * lands before the observer's — this does not re-derive that ordering, it
 * only preserves whatever order the response already carries.
 */

export interface AuditEntryTableProps {
  /** Handoff, Accessibility: "a caption naming the list." Differs per screen —
   *  "Audit log" here, "My activity" for task 8 — so it is not hard-coded. */
  caption: string;
  rows: readonly AuditEntry[];
  status: ListStatus;
  error?: { title?: string };
  empty?: { title?: string; body?: string };
  /** I-3: the filtered-narrows-it-to-nothing state, distinct from `empty`
   *  (nothing has ever happened). `AuditLogScreen` passes this; `MyActivity`
   *  has no filters and never needs it. */
  noResults?: NoResultsCopy;
  /** Forwarded to `DataTable`'s `NoResultsState` — the affordance an
   *  administrator needs to get back to an unfiltered view. */
  onClearFilters?: () => void;
  /**
   * Task 8's own column: the profile "my activity" screen shows only the
   * caller's own rows, so an Actor column would repeat the same name in
   * every cell. `false` (the default) keeps it — the administrator's full
   * trail spans every actor, and that is the one column that says who.
   */
  hideActorColumn?: boolean;
}

/**
 * `Omit<AuditEntry, 'id'>`, not a plain extension: `AuditEntry.id` is the
 * database's numeric primary key and `Row.id` is DataTable's own string row
 * identity (React's `key`, effectively) — the two cannot be the same
 * property, and `id` below is deliberately `primary.uuid`, never
 * `primary.id`.
 */
interface GroupedRow extends Row, Omit<AuditEntry, 'id'> {
  /** Every entry sharing this row's `request_id` (or the lone entry, if it
   *  had none), in the order the API returned them. A row can represent TWO
   *  OR MORE entries; `renderDetails` diffs every one of them via
   *  `hasDiff` (review round 1, M-1) rather than singling out one as
   *  "the" entry, which is why this interface no longer names a primary
   *  entry of its own (fix round 2: `primaryId` was dead — nothing read
   *  it once the all-entries diff replaced the `entries[0]`-only one). */
  entries: readonly AuditEntry[];
}

/** Decision V-8. See the file docblock. */
function groupByRequestId(rows: readonly AuditEntry[]): readonly GroupedRow[] {
  const order: string[] = [];
  const buckets = new Map<string, AuditEntry[]>();

  for (const entry of rows) {
    // `null` cannot collide with a real request id, but two DIFFERENT null
    // rows must not collide with EACH OTHER either — the `entry:` prefix
    // keys each on its own uuid instead of on the shared string "null".
    const key = entry.request_id ?? `entry:${entry.uuid}`;
    const bucket = buckets.get(key);
    if (bucket === undefined) {
      buckets.set(key, [entry]);
      order.push(key);
    } else {
      bucket.push(entry);
    }
  }

  return order.map((key) => {
    const entries = buckets.get(key) ?? [];
    const primary = entries[0];
    // `order` is built exclusively from keys that were just inserted with a
    // non-empty array, so `primary` is never undefined — the fallback below
    // exists only to satisfy the type checker, not because it can run.
    if (primary === undefined) throw new Error('unreachable: empty audit entry group');
    return { ...primary, id: primary.uuid, entries };
  });
}

export function AuditEntryTable({
  caption,
  rows,
  status,
  error,
  empty,
  noResults,
  onClearFilters,
  hideActorColumn = false,
}: AuditEntryTableProps) {
  const { t, i18n } = useTranslation();

  const grouped = useMemo(() => groupByRequestId(rows), [rows]);

  /**
   * Spec D-6: an audit entry expands in place to show the fields that
   * differ. State keyed by the row's `id` (the primary entry's `uuid`) —
   * plain component state is enough because this is view state local to one
   * render of the list, not anything a URL or another screen needs to share.
   */
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  function toggleExpanded(id: string) {
    setExpandedIds((was) => {
      const next = new Set(was);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /*
   * Human-readable: the date and time in the reader's language, with how
   * long ago it was beside it — on ONE line, so the cell is as tall as every
   * other table's and the row follows `--row-h` (two stacked lines made every
   * row 40px whatever the density, which read as compact even on
   * comfortable). The raw ISO-8601 value (with milliseconds and
   * offset — what an investigator correlating with server logs needs) stays
   * available as the cell's hover title rather than being what everyone reads.
   */
  function renderTime(row: Row): ReactNode {
    const { occurred_at: occurredAt } = row as GroupedRow;
    return (
      <span className={styles['time']} title={occurredAt}>
        <span>{formatTimestamp(occurredAt, i18n.language)}</span>
        <span className={styles['timeAgo']}>· {formatRelative(occurredAt, i18n.language)}</span>
      </span>
    );
  }

  function renderAction(row: Row): ReactNode {
    const { entries } = row as GroupedRow;
    return (
      <span className={styles['actionList']}>
        {entries.map((entry, index) => (
          <span key={entry.uuid}>
            {index > 0 ? <span aria-hidden="true"> · </span> : null}
            <bdi dir="ltr">{entry.action}</bdi>
          </span>
        ))}
      </span>
    );
  }

  function renderEntity(row: Row): ReactNode {
    const { entity_type: entityType, entity_id: entityId } = row as GroupedRow;
    if (entityType === null) return null;
    return <bdi dir="ltr">{entityId === null ? entityType : `${entityType} #${String(entityId)}`}</bdi>;
  }

  function renderResult(row: Row): ReactNode {
    const { result } = row as GroupedRow;
    return t(`audit.result.${result}`, { defaultValue: result });
  }

  function renderRequestId(row: Row): ReactNode {
    const { request_id: requestId } = row as GroupedRow;
    if (requestId === null) return null;
    return <bdi dir="ltr">{requestId}</bdi>;
  }

  /**
   * The before/after expander (spec D-6).
   *
   * Review round 1, M-1: diffs EVERY entry in the group that has one, not
   * only `entries[0]`. Decision V-8 exists because one administrator action
   * commonly writes TWO rows sharing a `request_id` — a semantic one and the
   * `AuditsChanges` trait's own field-level diff — and the trait's row is
   * written during the model's `save()`, before the controller's explicit
   * `record()`, so it is almost never the entry that arrives first. Reaching
   * for `entries[0]` alone (as this file's own docblock already warns) would
   * show the wrong entry's diff, or none at all, exactly when the real
   * attribute-level change lives on the entry that is NOT primary.
   *
   * `hasDiff`, not "state is non-null", decides which entries qualify: a
   * ruling from review round 1 is that a toggle whose panel would render
   * nothing (both sides recorded but identical) must not appear at all —
   * `DataTable`'s own empty-cell contract already reads correctly for that
   * case, and a control that says "Show what changed" and opens onto
   * nothing is worse than no control.
   *
   * Task 3 (M-19), closing M-18 finding M-4: the toggle stays in the Details
   * column, but the PANEL no longer renders inside that cell. It used to —
   * the rightmost cell of a table with a 900px min-width that scrolls
   * sideways past a sticky first column, exactly the wrong place for
   * canonical JSON, hashes and now SMS template bodies. `diffableEntriesOf`
   * below is shared by the toggle (which only needs to know whether one
   * exists, for the empty-cell contract) and by `renderExpansion` (which
   * renders the panel itself, in DataTable's own full-width row).
   */
  function diffableEntriesOf(row: Row): readonly AuditEntry[] {
    const { entries } = row as GroupedRow;
    return entries.filter((entry) => hasDiff(entry.before_state, entry.after_state));
  }

  function renderDetails(row: Row): ReactNode {
    const { id } = row as GroupedRow;
    const diffable = diffableEntriesOf(row);

    if (diffable.length === 0) return null;

    const expanded = expandedIds.has(id);
    const detailId = `audit-entry-detail-${id}`;
    // Review round 1, m-2: every row's toggle previously shared the single
    // accessible name "Show what changed" — a screen reader tabbing a
    // 25-row column heard the same name 25 times, tied to nothing. Folding
    // in the diffable entries' own action name(s) disambiguates without a
    // second `aria-labelledby` wired into the Action column's cell.
    const actionLabel = diffable.map((entry) => entry.action).join(', ');

    return (
      <Button
        type="button"
        /*
         * A LABELLED BUTTON, NOT A BARE CARET, and this is the fix for a
         * real report: "the audit log does not show before -> after". It
         * did — behind an icon-only toggle, in the last column, on the
         * minority of rows that actually carry a state diff. Nothing on
         * screen said the diff existed, so nobody found it. The caret
         * stays as the open/closed indicator; the words are what make it
         * a control someone can see.
         */
        variant="secondary"
        aria-expanded={expanded}
        // m-1: an IDREF only while the region it names actually exists —
        // dropped rather than left dangling at a collapsed (or removed)
        // element. The region itself now lives in DataTable's expansion
        // row rather than beside this button, but an `aria-controls` IDREF
        // works the same regardless of which element in the document it
        // names.
        {...(expanded ? { 'aria-controls': detailId } : {})}
        aria-label={t(expanded ? 'audit.detail.collapse' : 'audit.detail.expand', {
          action: actionLabel,
        })}
        icon={
          expanded ? (
            <CaretUpIcon size={16} weight="fill" />
          ) : (
            <CaretDownIcon size={16} weight="fill" />
          )
        }
        onClick={() => {
          toggleExpanded(id);
        }}
      >
        {t(expanded ? 'audit.detail.hideLabel' : 'audit.detail.showLabel')}
      </Button>
    );
  }

  /**
   * The panel itself, moved onto `DataTable`'s full-width expansion row
   * (task 3). Returns `null` whenever the row has nothing diffable or is not
   * expanded, which tells `DataTable` to emit no expansion row at all for
   * that row — the group with no diff gets no extra row, exactly as it got
   * no toggle before.
   */
  function renderExpansion(row: Row): ReactNode {
    const { id } = row as GroupedRow;
    if (!expandedIds.has(id)) return null;

    const diffable = diffableEntriesOf(row);
    if (diffable.length === 0) return null;

    const detailId = `audit-entry-detail-${id}`;

    return (
      <div id={detailId} className={styles['detailRegion']}>
        {diffable.map((entry) => (
          <div key={entry.uuid}>
            {/* Labelled only when a reader could otherwise not tell
                which entry's diff they are looking at. */}
            {diffable.length > 1 ? (
              <p className={styles['entryLabel']}>
                {t('audit.detail.changesFor', { action: entry.action })}
              </p>
            ) : null}
            <AuditEntryDetail beforeState={entry.before_state} afterState={entry.after_state} />
          </div>
        ))}
      </div>
    );
  }

  // Not memoised: every `render*` closure above is rebuilt each render
  // anyway (they read `t` fresh), so a `useMemo` here would only add a
  // dependency array to keep honest for no saved work.
  const columns: readonly Column[] = [
    { key: 'occurred_at', label: t('audit.columnTime'), render: renderTime },
    ...(hideActorColumn ? [] : [{ key: 'actor_label', label: t('audit.columnActor') }]),
    { key: 'action', label: t('audit.columnAction'), render: renderAction },
    { key: 'entity', label: t('audit.columnEntity'), render: renderEntity },
    { key: 'result', label: t('audit.columnResult'), render: renderResult },
    { key: 'request_id', label: t('audit.columnRequestId'), render: renderRequestId, neverMirrors: true },
    { key: 'details', label: t('audit.columnDetails'), render: renderDetails },
  ];

  return (
    <DataTable
      caption={caption}
      columns={columns}
      rows={grouped}
      status={status}
      // No `totalCount`: that prop's documented contract (`DataTable.tsx`)
      // is "how many rows exist behind the current filters", a fact cursor
      // pagination has no way to know. `totalCount ?? rows.length` inside
      // `DataTable` already renders the page count on its own — passing
      // `grouped.length` here said the same number while claiming a total
      // this screen cannot support.
      empty={empty}
      noResults={noResults}
      onClearFilters={onClearFilters}
      error={error}
      renderExpansion={renderExpansion}
    />
  );
}
